import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Search, Ban, CheckCircle2, Download, EyeOff, Eye, ChevronRight, Stethoscope } from "lucide-react";
import { Empty } from "./_shared";

function fmtDay(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}
function lastActive(u) {
  const ls = u.last_seen_at || u.last_active_at || u.last_login_at;
  if (!ls) return { label: "nie", dot: "bg-muted-foreground/40" };
  const days = Math.floor((Date.now() - new Date(ls).getTime()) / 86400000);
  if (days <= 0) return { label: "heute", dot: "bg-emerald-500" };
  if (days <= 7) return { label: `vor ${days} T`, dot: "bg-emerald-500" };
  if (days <= 30) return { label: `vor ${days} T`, dot: "bg-amber-500" };
  return { label: `vor ${days} T`, dot: "bg-rose-500" };
}
function founderBadge(u) {
  const c = u.founder_grant?.cohort;
  if (c === "founder_500") return "F500";
  if (c === "founder_50" || u.founder_50) return "F50";
  return null;
}

export default function Users() {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [active, setActive] = useState("");
  const [vis, setVis] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showDiag, setShowDiag] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (role) p.set("role", role);
      if (active) p.set("active", active);
      if (vis) p.set("include_hidden", "true");
      p.set("limit", "100");
      const { data } = await api.get(`/admin/users?${p.toString()}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  }, [q, role, active, vis]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const quick = async (fn, uid) => {
    setBusy(uid);
    try { await fn(); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };
  const setActiveState = (e, u, next) => { e.preventDefault(); e.stopPropagation(); quick(() => api.post(`/admin/users/${u.id}/set-active`, { active: next }), u.id); };
  const toggleHidden = (e, u) => { e.preventDefault(); e.stopPropagation(); quick(() => api.post(`/admin/users/${u.id}/toggle-hidden`), u.id); };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/users.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "users.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader eyebrow="Admin" title="Nutzer" subtitle="Klicke auf einen Nutzer für alle Details & Aktionen."
        action={
          <div className="flex gap-2">
            <Button variant={showDiag ? "default" : "outline"} size="sm" className="gap-1.5 rounded-full" onClick={() => setShowDiag((v) => !v)} data-testid="admin-users-diag-toggle">
              <Stethoscope className="h-4 w-4" /> Diagnose
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={exportCsv} data-testid="admin-users-export"><Download className="h-4 w-4" /> CSV</Button>
          </div>
        } />

      {showDiag && <Diagnostics />}
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name oder E-Mail…" className="pl-9" data-testid="admin-user-search" />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-2 text-sm text-foreground" data-testid="admin-user-role">
          <option value="">Alle Rollen</option><option value="client">Client</option><option value="coach">Coach</option><option value="admin">Admin</option>
        </select>
        <select value={active} onChange={(e) => setActive(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-2 text-sm text-foreground" data-testid="admin-user-active">
          <option value="">Alle Status</option><option value="true">Aktiv</option><option value="false">Gesperrt</option>
        </select>
        <select value={vis} onChange={(e) => setVis(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-2 text-sm text-foreground" data-testid="admin-user-visibility">
          <option value="">Nur sichtbar</option><option value="1">Inkl. versteckt</option>
        </select>
      </div>

      {/* Column header (desktop) */}
      <div className="hidden px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 lg:grid lg:grid-cols-[1.8fr_0.8fr_1fr_1fr_0.7fr_auto] lg:gap-3">
        <span>Nutzer</span><span>Plan</span><span>Registriert</span><span>Zuletzt aktiv</span><span>Founder</span><span className="text-right">Aktionen</span>
      </div>

      {loading ? <Skeleton className="h-64 rounded-2xl" /> : rows.length === 0 ? <Empty text="Keine Nutzer gefunden." /> : (
        <div className="space-y-2">
          {rows.map((u) => {
            const la = lastActive(u);
            const fb = founderBadge(u);
            return (
              <Link
                key={u.id}
                to={`/app/admin/users/${u.id}`}
                data-testid={`admin-user-${u.id}`}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:bg-accent/40 lg:grid lg:grid-cols-[1.8fr_0.8fr_1fr_1fr_0.7fr_auto]"
              >
                <div className="min-w-0 flex-1 lg:flex-none">
                  <p className="truncate text-sm font-medium text-foreground">
                    {u.name || "—"}{u.active === false && <span className="ml-1 rounded bg-rose-100 px-1.5 text-[10px] text-rose-700">gesperrt</span>}{u.is_hidden_in_admin && <span className="ml-1 rounded bg-secondary px-1.5 text-[10px] text-muted-foreground">versteckt</span>}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{u.email} · {u.role}</p>
                </div>
                <div className="text-xs"><span className="rounded-full bg-secondary px-2 py-0.5 font-medium">{u.app_subscription?.tier || "free"}</span></div>
                <div className="hidden text-xs text-muted-foreground lg:block">{fmtDay(u.created_at)}</div>
                <div className="hidden items-center gap-1.5 text-xs text-muted-foreground lg:flex">
                  <span className={`inline-block h-2 w-2 rounded-full ${la.dot}`} /> {la.label}
                </div>
                <div className="text-xs">{fb ? <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700">{fb}</span> : <span className="text-muted-foreground/40">—</span>}</div>
                <div className="flex items-center gap-1.5 lg:justify-end">
                  <Button size="sm" variant="outline" className="h-8 w-8 rounded-full p-0" disabled={busy === u.id} onClick={(e) => toggleHidden(e, u)} data-testid={`admin-hide-${u.id}`}>
                    {u.is_hidden_in_admin ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                  {u.active === false
                    ? <Button size="sm" variant="outline" className="h-8 w-8 rounded-full p-0 text-emerald-600" disabled={busy === u.id} onClick={(e) => setActiveState(e, u, true)} data-testid={`admin-activate-${u.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                    : <Button size="sm" variant="outline" className="h-8 w-8 rounded-full p-0 text-rose-600" disabled={busy === u.id} onClick={(e) => setActiveState(e, u, false)} data-testid={`admin-suspend-${u.id}`}><Ban className="h-4 w-4" /></Button>}
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DiagStat({ label, value, tone }) {
  const t = tone === "bad" ? "text-rose-600" : tone === "warn" ? "text-amber-600" : tone === "good" ? "text-emerald-600" : "text-foreground";
  return (
    <div className="rounded-xl border border-border bg-background p-3" data-testid={`admin-diag-stat-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`font-display text-2xl font-semibold ${t}`}>{value ?? "—"}</p>
    </div>
  );
}

function Diagnostics() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("test");
  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const { data } = await api.get("/admin/user-diagnostics?limit=2000"); setD(data); }
      catch (err) { toast.error(apiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <Skeleton className="h-48 rounded-2xl" />;
  if (!d) return null;
  const td = d.test_demo || {};
  const dup = d.duplicates || {};
  const dupEmail = dup.by_email_exact || [];
  const dupPerson = dup.by_email_same_person || [];
  const dupName = dup.by_name || [];
  const tabs = [
    ["test", `Test/Demo (${td.count ?? 0})`],
    ["dupes", `Duplikate (${dupEmail.length})`],
    ["names", `Namensgleich (${dupName.length})`],
  ];
  return (
    <div className="space-y-3 rounded-2xl border border-border bg-card p-4" data-testid="admin-user-diagnostics">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <DiagStat label="Nutzer gesamt" value={d.total} />
        <DiagStat label="Echte (geschätzt)" value={d.real_estimate} tone="good" />
        <DiagStat label="Test/Demo/Seed" value={td.count} tone="warn" />
        <DiagStat label="Gäste (anonym)" value={d.anon_guest} />
        <DiagStat label="Gesperrt" value={d.suspended} tone={d.suspended ? "warn" : undefined} />
        <DiagStat label="Doppelte E-Mails" value={dupEmail.length} tone={dupEmail.length ? "bad" : "good"} />
      </div>
      <p className="text-[11px] text-muted-foreground" data-testid="admin-diag-roles">
        Rollen: {Object.entries(d.by_role || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
      </p>
      <div className="flex gap-1 border-b border-border">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-diag-tab-${k}`}
            className={`px-3 py-1.5 text-xs font-medium ${tab === k ? "border-b-2 border-primary text-foreground" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "test" && (
        <div className="max-h-96 space-y-1.5 overflow-auto" data-testid="admin-diag-test-list">
          {td.truncated && <p className="text-[11px] text-amber-600">Zeige {td.returned} von {td.count} — Rest gekürzt.</p>}
          {(td.accounts || []).map((a) => (
            <Link key={a.id} to={`/app/admin/users/${a.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background p-2 hover:bg-accent/40" data-testid={`admin-diag-acct-${a.id}`}>
              <div className="min-w-0">
                <p className="truncate text-xs font-medium text-foreground">{a.name || "—"} · {a.role} · <span className="text-muted-foreground">{a.tier}</span></p>
                <p className="truncate text-[11px] text-muted-foreground">{a.email || "(keine E-Mail)"}</p>
              </div>
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-700">{a.reason}</span>
            </Link>
          ))}
          {!(td.accounts || []).length && <Empty text="Keine Test-/Demokonten gefunden." />}
        </div>
      )}

      {tab === "dupes" && (
        <div className="max-h-96 space-y-2 overflow-auto" data-testid="admin-diag-dupe-list">
          {!dupEmail.length ? <Empty text="Keine doppelten E-Mails — sauber." /> : dupEmail.map((g) => (
            <div key={g.email} className="rounded-lg border border-rose-200 bg-rose-50 p-2" data-testid={`admin-diag-dupe-${g.email}`}>
              <p className="text-xs font-medium text-rose-800">{g.email} <span className="font-normal">· {g.count}× angelegt</span></p>
            </div>
          ))}
          {dupPerson.length > 0 && (
            <>
              <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Mögliche Mehrfachkonten (gleiche Mailbox)</p>
              {dupPerson.map((g) => (
                <div key={g.normalized} className="rounded-lg border border-amber-200 bg-amber-50 p-2">
                  <p className="text-xs text-amber-900">{(g.emails || []).join(", ")} <span className="font-normal">· {g.count} Varianten</span></p>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {tab === "names" && (
        <div className="max-h-96 space-y-1.5 overflow-auto" data-testid="admin-diag-name-list">
          {!dupName.length ? <Empty text="Keine Namensdubletten." /> : dupName.map((g) => (
            <div key={g.name} className="flex items-center justify-between rounded-lg border border-border bg-background p-2">
              <p className="text-xs capitalize text-foreground">{g.name}</p>
              <span className="text-[11px] text-muted-foreground">{g.count}×</span>
            </div>
          ))}
          <p className="pt-1 text-[11px] text-muted-foreground">Gleiche Namen sind oft normal — nur „Doppelte E-Mails“ sind echte Dubletten.</p>
        </div>
      )}
    </div>
  );
}

